import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ConfirmationService, MessageService, SharedModule } from 'primeng/api';
import { Button } from 'primeng/button';
import { IconField } from 'primeng/iconfield';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { Tooltip } from 'primeng/tooltip';
import { debounceTime, Subject } from 'rxjs';
import Page from '../../../../types/shared/Page';
import VehicleModel from '../../../../types/vehicle-model/VehicleModel';
import VehicleModelWriteDTO from '../../../../types/vehicle-model/VehicleModelWriteDTO';
import { IsAdminDirective } from '../../directives/is-admin.directive';
import { ExportService } from '../../services/export.service';
import { VehicleModelService } from '../../services/vehicle-model.service';
import { PageHeaderComponent } from '../shared/page-header/page-header.component';
import {
    CreateVehicleModelDialogComponent
} from '../shared/vehicle-models/create-vehicle-model-dialog/create-vehicle-model-dialog.component';
import {
    UpdateVehicleModelDialogComponent
} from '../shared/vehicle-models/update-vehicle-model-dialog/update-vehicle-model-dialog.component';

@Component({
    selector: 'app-vehicle-models',
    templateUrl: './vehicle-models.component.html',
    styleUrls: [ './vehicle-models.component.scss' ],
    imports: [
        PageHeaderComponent,
        IconField,
        InputIcon,
        FormsModule,
        TableModule,
        Button,
        SharedModule,
        InputText,
        RouterLink,
        CreateVehicleModelDialogComponent,
        UpdateVehicleModelDialogComponent,
        IftaLabelModule,
        IsAdminDirective,
        Tooltip
    ]
})
export class VehicleModelsComponent implements OnInit {
    public search = '';
    public vehicleModels: VehicleModel[] = [];
    public totalRecords = 0;
    public rows = 5;
    public currentPage = 0;
    public loading = false;
    public displayCreateVehicleModelDialog = false;
    public displayUpdateVehicleModelDialog = false;
    public selectedVehicleModel: VehicleModelWriteDTO = new VehicleModelWriteDTO({});

    private searchSubject = new Subject<string>();

    constructor(
        private vehicleModelService: VehicleModelService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private exportService: ExportService
    ) {
    }

    ngOnInit(): void {
        this.loadVehicleModels();

        // Debounce the search input to prevent too many requests
        this.searchSubject.pipe(debounceTime(300)).subscribe(() => {
            this.currentPage = 0; // Reset to the first page on new search
            this.loadVehicleModels();
        });
    }

    loadVehicleModels(): void {
        this.loading = true;

        const size = this.rows;
        const search = this.search || '';

        this.vehicleModelService
            .getVehicleModels(search, this.currentPage, size)
            .then((result: Page<VehicleModel>) => {
                this.vehicleModels = result.content;
                this.totalRecords = result.page.totalElements;
            })
            .catch((error: any) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
            })
            .finally(() => {
                this.loading = false;
            });
    }

    onPageChange(event: any): void {
        this.currentPage = Math.floor(event.first / event.rows);
        this.rows = event.rows;
        this.loadVehicleModels();
    }

    onSearchInputChange(): void {
        this.searchSubject.next(this.search);
    }

    editVehicleModel(vehicleModel: VehicleModel): void {
        this.selectedVehicleModel = new VehicleModelWriteDTO(vehicleModel);
        this.selectedVehicleModel.brandId = vehicleModel.brand?.id;
        this.selectedVehicleModel.bodyTypeId = vehicleModel.bodyType?.id;
        this.selectedVehicleModel.carClassId = vehicleModel.carClass?.id;
        this.displayUpdateVehicleModelDialog = true;
    }

    deleteVehicleModel(vehicleModel: VehicleModel): void {
        this.confirmationService.confirm({
            message: `Are you sure you want to delete the vehicle model '${ vehicleModel.modelSeries }'?`,
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.vehicleModelService.deleteVehicleModel(vehicleModel.id).then(() => {
                    this.loadVehicleModels();
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Vehicle model deleted',
                        life: 3000
                    });
                }).catch((error) => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
                });
            }
        });
    }

    openCreateVehicleModelDialog(): void {
        this.displayCreateVehicleModelDialog = true;
    }

    reloadVehicleModels(): void {
        this.loadVehicleModels();
    }

    public exportVehicleModels() {
        this.exportService.exportVehicleModels()
            .then(() => {
                this.messageService.add(
                    { severity: 'success', summary: 'Success', detail: 'Vehicle models exported', life: 3000 });
            })
            .catch((error) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
            });
    }
}
